<template>
	<div class="grouplobby">
		<SimpleLoader theme="mainColor_normal"
			v-if="loading"
			class="loader"
			big />

		<div v-if="!loading && !serverReboot" class="holder">
			<div class="playlists header">
				<h1>{{$t('group.lobby.title')}}</h1>
				<div v-for="p in sortedPlaylists" :key="p.id" class="playlist">
					<img :src="p.cover" :alt="p.name" class="cover">
					<span class="label">{{p.name}}</span>
				</div>
			</div>

			<Button :title="$t('group.lobby.start')"
				class="start"
				type="button"
				:icon="$getIcon('play')"
				big
				:disabled="room.users.length < 2 || (expertMode != null && expertMode.length == 0)"
				v-if="isHost"
				@click="startGame()" />

			<div class="users">
				<h2 class="highlight">{{$t('group.lobby.players')}}</h2>
				<div class="content">
					<div class="list" v-if="room.users.length > 0">
						<GroupLobbyUser
							v-for="u in room.users"
							:key="u.id"
							:data="u"
							:me="me"
							:isHost="u.id == room.creator"
							@update="onUpdateUserHandicap"
							@updateName="onUpdateUserName"
						/>
					</div>

					<form @submit.prevent="onSubmit()" class="form" v-if="!me">
						<label for="username">{{$t('group.lobby.join')}}</label>
						<div class="row">
							<input type="text" id="username" class="dark" v-model="userName" maxlength="30" :placeholder="$t('group.lobby.usernamePlaceholder')" v-focus>
							<Button :icon="$getIcon('checkmark_white')" class="submit" type="submit" :disabled="userName.length < 3" :loading="joining" />
						</div>
					</form>
				</div>
			</div>


			<GameParams v-if="isHost" class="params"
				:gamesCount.sync="gamesCount"
				:gameDuration.sync="gameDuration"
				:tracksCount.sync="tracksCount"
				:acceptAlbum.sync="acceptAlbum"
				:expertMode.sync="expertMode">
			</GameParams>

			<SimpleLoader theme="mainColor_normal"
				v-if="!isHost && room.users.length > 0"
				class="waitHost"
				:label="$t('group.lobby.wait', {hostName:hostName})" />

			<ShareMultiplayerLink v-if="room" class="shareUrl" />
		</div>

		<div v-if="serverReboot" class="serverReboot">
			<div v-html="$t('group.game.serverReboot')"></div>
			<Button :title="$t('global.back')" class="back" white :to="{name:'home'}" />
		</div>
	</div>
</template>

<script lang="ts">
import GameParams from "@/components/GameParams.vue";
import { Component, Prop, Vue } from "vue-property-decorator";
import Button from '../components/Button.vue';
import ExpertModeForm from '../components/ExpertModeForm.vue';
import GroupLobbyUser from '../components/GroupLobbyUser.vue';
import IncrementForm from '../components/IncrementForm.vue';
import ShareMultiplayerLink from '../components/ShareMultiplayerLink.vue';
import SimpleLoader from '../components/SimpleLoader.vue';
import SockController, { SOCK_ACTIONS } from '../sock/SockController';
import Api from '../utils/Api';
import RoomData from '../vo/RoomData';
import SocketEvent from '../vo/SocketEvent';
import UserData from '../vo/UserData';

@Component({
	components:{
		Button,
		GameParams,
		SimpleLoader,
		IncrementForm,
		ExpertModeForm,
		GroupLobbyUser,
		ShareMultiplayerLink,
	}
})
export default class GroupLobby extends Vue {

	@Prop()
	public id:string;

	public gamesCount:number = 5;
	public tracksCount:number = 4;
	public gameDuration:number = 60;
	public acceptAlbum:boolean = false;
	public expertMode:string[] = null;
	public showCopied:boolean = false;
	public loading:boolean = true;
	public joining:boolean = false;
	public serverReboot:boolean = false;
	public room:RoomData = null;
	public userName:string = "";

	public joinHandler:any;
	public leaveHandler:any;
	public startGameHandler:any;
	public handicapHandler:any;
	public updateUserNameHandler:any;
	public serverRebootHandler:any;

	//Sort playlists by name size for a better looking list rendering
	public get sortedPlaylists():any[] {
		return this.room.playlists.sort((a, b) => {
			if(a.name > b.name) return 1;
			if(a.name < b.name) return -1;
			return 0;
		})
	}

	public get isHost():boolean {
		if(!this.me) return false;
		return this.room.creator == this.me.id;
	}

	public get me():UserData {
		return this.$store.state.userGroupData;
	}

	public get hostName():string {
		for (let i = 0; i < this.room.users.length; i++) {
			if(this.room.creator == this.room.users[i].id) {
				return this.room.users[i].name;
			}
		}
		return null;
	}

	public mounted():void {
		this.joinHandler = (e) => this.onJoin(e);
		this.leaveHandler = (e) => this.onLeave(e);
		this.startGameHandler = (e) => this.onStartGame(e);
		this.handicapHandler = (e) => this.onHandicap(e);
		this.updateUserNameHandler = (e) => this.onUserName(e);
		this.serverRebootHandler = (e) => this.onServerReboot(e);
		SockController.instance.addEventListener(SOCK_ACTIONS.JOIN_ROOM, this.joinHandler);
		SockController.instance.addEventListener(SOCK_ACTIONS.LEAVE_ROOM, this.leaveHandler);
		SockController.instance.addEventListener(SOCK_ACTIONS.START_GROUP_GAME, this.startGameHandler);
		SockController.instance.addEventListener(SOCK_ACTIONS.UPDATE_HANDICAP, this.handicapHandler);
		SockController.instance.addEventListener(SOCK_ACTIONS.UPDATE_USERNAME, this.updateUserNameHandler);
		SockController.instance.addEventListener(SOCK_ACTIONS.SERVER_REBOOT, this.serverRebootHandler);
		this.loadDetails();
	}

	public beforeDestroy():void {
		//Cleanup listeners
		SockController.instance.removeEventListener(SOCK_ACTIONS.JOIN_ROOM, this.joinHandler);
		SockController.instance.removeEventListener(SOCK_ACTIONS.LEAVE_ROOM, this.leaveHandler);
		SockController.instance.removeEventListener(SOCK_ACTIONS.START_GROUP_GAME, this.startGameHandler);
		SockController.instance.removeEventListener(SOCK_ACTIONS.UPDATE_HANDICAP, this.handicapHandler);
		SockController.instance.removeEventListener(SOCK_ACTIONS.UPDATE_USERNAME, this.updateUserNameHandler);
		SockController.instance.removeEventListener(SOCK_ACTIONS.SERVER_REBOOT, this.serverRebootHandler);
	}

	/**
	 * Load group's details
	 */
	public async loadDetails():Promise<void> {
		try {
			let res = await Api.get("group/details", {roomId:this.id});
			this.room = res.room;
		}catch(error) {
			this.$store.dispatch("alert", (error as {message:string}).message);
			if(this.$store.state.loggedin) {
				this.$router.push({name:"playlists", params:{mode:"multi"}});
			}else{
				this.$router.push({name:"home"});
			}
			return;
		}
		this.loading = false;
		if(this.me) {
			this.onSubmit();//Auto join room
		}
		if(this.room) {
			SockController.instance.groupId = this.room.id;
		}
	}

	/**
	 * Called when subitting username form
	 */
	public async onSubmit():Promise<void> {
		this.joining = true;
		var res;
		let data:any = {
				username:this.userName,
				roomId:this.room.id
			};
		if(this.me) {
			delete data.username;
			data.user = this.me;
		}
		try {
			res = await Api.post("group/join", data);
			this.room = res.room;
		}catch(error) {
			this.joining = false;
			this.$store.dispatch("alert", (error as {message:string}).message);
			return;
		}
		res.me.offline = false;
		this.$store.dispatch("setUserGroupData", res.me);
		this.joining = false;
		if(this.room.currentTracks) {
			this.onStartGame();
		}
		if(this.room) {
			SockController.instance.groupId = this.room.id;
		}

		if(this.$route.meta.restartMode) {
			SockController.instance.sendMessage({action:SOCK_ACTIONS.RESTART_GROUP_GAME, data:{roomId:this.room.id}})
		}
	}

	/**
	 * Called when someone joins the room
	 */
	public onJoin(e:SocketEvent):void {
		if(!this.room) return;
		let found = false;
		for (let i = 0; i < this.room.users.length; i++) {
			if(this.room.users[i].id == e.data.user.id) {
				found = true;
				this.room.users[i].offline = false;
			}
		}
		if(!found) {
			this.room.users.push(e.data.user);
		}
	}
	
	/**
	 * Called when someone leaves the room
	 */
	public onLeave(e:SocketEvent):void {
		if(!this.room) return;
		for (let i = 0; i < this.room.users.length; i++) {
			const u = this.room.users[i];
			if(u.id == e.data.user.id) {
				this.room.users[i].offline = true;
			}
		}
	}

	/**
	 * Called when socket tells to start the game
	 */
	public onStartGame(e?:SocketEvent):void {
		let data = e? e.data : this.room;
		this.$store.dispatch("setGroupRoomData", data);//This is only used to transmit data to game view
		this.$router.push({name:"group/play"});
	}

	/**
	 * Called when a user's handicap is updated
	 */
	public onHandicap(e:SocketEvent):void {
		for (let i = 0; i < this.room.users.length; i++) {
			const u = this.room.users[i];
			if(u.id == e.data.user.id) {
				Vue.set(u, "handicap", e.data.handicap);
			}
		}
	}

	/**
	 * Called when a user's name is updated
	 */
	public onUserName(e:SocketEvent):void {
		for (let i = 0; i < this.room.users.length; i++) {
			const u = this.room.users[i];
			if(u.id == e.data.user.id) {
				Vue.set(u, "name", e.data.user.name);
			}
		}
	}

	/**
	 * Called when clicking "start" button.
	 * It sends a socket event to all users including self
	 * See onStartGame() method
	 */
	public startGame():void {
		this.room.tracksCount = this.tracksCount;
		this.room.gamesCount = this.gamesCount;
		this.room.gameDuration = this.gameDuration;
		this.room.acceptAlbum = this.acceptAlbum;
		this.room.expertMode = this.expertMode;
		Api.post("group/update", {room:this.room, callerId:this.me.id});
		SockController.instance.sendMessage({action:SOCK_ACTIONS.START_GROUP_GAME, includeSelf:true, data:this.room});
	}

	/**
	 * Handicap of a user updated
	 */
	public onUpdateUserHandicap(user:UserData, handicap:number):void {
		user.handicap = handicap;
		SockController.instance.sendMessage({action:SOCK_ACTIONS.UPDATE_HANDICAP, data:{user:user, handicap:handicap, groupId:this.room.id}});
	}

	/**
	 * Called when a user updates her/his nickname
	 */
	public onUpdateUserName(user:UserData):void {
		this.$store.dispatch("setUserName", user.name);
		SockController.instance.sendMessage({action:SOCK_ACTIONS.UPDATE_USERNAME, data:{user:user, groupId:this.room.id}});
	}

	/**
	 * Called when server has reboot
	 */
	public onServerReboot(e:SocketEvent):void {
		this.serverReboot = true;
	}

}
</script>

<style scoped lang="less">
.grouplobby{
	.loader {
		.center();
		position: absolute;
		transform: translate(-50%, -50%);
	}

	&>.holder {
		&>div {
			box-sizing: border-box;
		}
		.playlists {
			display: flex;
			flex-direction: column;
			align-items: center;
	
			h1 {
				margin-bottom: 20px;
			}
	
			.playlist {
				background-color: @mainColor_normal;
				border-radius: 100px;
				display: flex;
				flex-direction: row;
				align-items: center;
				padding: 5px 10px 5px 5px;
				box-sizing: border-box;
				width: min-content;
				white-space: nowrap;
				max-width: 100%;
				margin-bottom: 5px;
				.label {
					color: #fff;
					margin-left: 5px;
					overflow: hidden;
					text-overflow: ellipsis;
				}
				.cover {
					width: 30px;
					height: 30px;
					border-radius: 50%;
					object-fit: cover;
				}
			}
		}
	
		.start {
			display: flex;
			margin: auto;
			margin-top: 25px;
			margin-bottom: 25px;
		}
	
		.users {
			// width: 200px;
			width: min-content;
			margin: auto;
			min-width: 300px;
			.content {
				.blockContent();
				padding-bottom: 0px;

				.list {
					width: 100%;
					padding-bottom: 10px;
				}
				
				.form {
					display: flex;
					flex-direction: column;
					padding-bottom: 10px;
					.row {
						display: flex;
						flex-direction: row;
						input {
							flex-grow: 1;
							border-top-right-radius: 0;
							border-bottom-right-radius: 0;
						}
						.submit {
							width: 39px;
							border-top-left-radius: 0;
							border-bottom-left-radius: 0;
						}
					}
				}
			}
		}
	
		.waitHost {
			margin-top: 25px;
		}
	
		.shareUrl {
			margin:auto;
			margin-top: 25px;
		}
	}

	.backHome {
		margin: auto;
		margin-top: 25px;
		width: min-content;
		display: block;
	}

	.params {
		margin: auto;
		margin-top: 25px;
		display: flex;
		flex-direction: column;
		width: min-content;
		.content {
			.blockContent();
			.increment:not(:last-child) {
				margin-bottom: 20px;
			}
		}
	}
	
	.serverReboot {
		.center();
		position: absolute;
		color: #FFFFFF;
		background-color: @mainColor_warn;
		padding: 20px;
		border-radius: 20px;
		text-align: center;
		font-size: 24px;
		font-family: "Futura";

		.back {
			margin-top: 10px;
		}
	}
}
</style>